import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { useNoticeDetail } from '~/features/notice/queries/useNotice'
import NoticeBgMobile from '~/shared/assets/background/notice-bg-mobile.png'
import NoticeBgPc from '~/shared/assets/background/notice-bg-pc.png'
import ArrowLeft from '~/shared/assets/icons/ArrowLeft'
import ArrowRight from '~/shared/assets/icons/ArrowRight'
import Back from '~/shared/assets/icons/Back'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import useViewport from '~/shared/hooks/useViewport'
import theme from '~/shared/styles/theme'
import { fDate } from '~/shared/utils/format-date'

function NoticeDetailPage() {
  const viewport = useViewport()
  const isMobile = viewport < theme.deviceSizes.medium
  const backgroundImage = isMobile ? NoticeBgMobile : NoticeBgPc

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading } = useNoticeDetail(id!)

  if (isLoading) return <LoadingScreen />

  const { info, navigationInfo } = data!

  const handlePrevClick = () => {
    if (navigationInfo.prevObj) {
      navigate(`/notice/${navigationInfo.prevObj.id}`)
    }
  }

  const handleNextClick = () => {
    if (navigationInfo.nextObj) {
      navigate(`/notice/${navigationInfo.nextObj.id}`)
    }
  }

  return (
    <Wrapper>
      <BackgroundImage src={backgroundImage} alt="background image" />
      <Container>
        <BackBox onClick={() => navigate('/notice')}>
          <Back />
          <span className="text">돌아가기</span>
        </BackBox>
        <TitleBox>
          <TitleTag>{info.category_desc}</TitleTag>
          <Title>{info.title}</Title>
        </TitleBox>
        <Line />
        <WriteDate>{`작성일 : ${fDate(info.updated_at, 'yyyy.MM.dd')}`}</WriteDate>
        <Body
          dangerouslySetInnerHTML={{
            __html: info.body || '',
          }}
        />
        <Line />
        <NavigationContainer>
          {navigationInfo.prevObj && (
            <NavigationBox>
              <NavigationButtonBox onClick={handlePrevClick}>
                <ArrowLeft />
                <span>이전글</span>
              </NavigationButtonBox>
              {!isMobile && (
                <NavigationText>{navigationInfo.prevObj.title}</NavigationText>
              )}
            </NavigationBox>
          )}
          {navigationInfo.nextObj && (
            <NavigationBox>
              {!isMobile && (
                <NavigationText>{navigationInfo.nextObj.title}</NavigationText>
              )}
              <NavigationButtonBox onClick={handleNextClick}>
                <ArrowRight />
                <span>다음글</span>
              </NavigationButtonBox>
            </NavigationBox>
          )}
        </NavigationContainer>
      </Container>
    </Wrapper>
  )
}

export default NoticeDetailPage

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: flex-start;

  box-sizing: border-box;

  /* width: fit-content; */
  width: 100%;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 0 auto;
  margin-top: calc(80px + ${theme.size.headerHeight}px);
  margin-bottom: 120px;

  @media ${theme.device.medium} {
    gap: 24px;
    align-items: start;
    width: 100%;
    margin-top: calc(56px + ${theme.size.headerHeight}px);
  }
`

const BackgroundImage = styled.img`
  position: absolute;
  z-index: ${theme.zIndex.belowBackground};
  top: 0;
  left: 0;

  overflow: hidden;

  width: 100%;
  height: 100%;

  background-repeat: no-repeat;

  @media ${theme.device.medium} {
    height: auto;
    background-repeat: repeat;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: start;

  box-sizing: border-box;
  width: 100%;
  padding: 36px 24px;

  background-color: white;
  border-radius: 12px;
`

const BackBox = styled.div`
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: center;

  .text {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }
`

const TitleBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  @media ${theme.device.medium} {
    flex-direction: column;
    align-items: start;
  }
`

const TitleTag = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 100%;
  max-width: 164px;
  height: 41px;
  padding: 0 10px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: #313634;

  background-color: #eaebeb;
  border-radius: 60px;
`

const Title = styled.h2`
  max-width: 900px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 58px;
  color: #313634;
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #c1c3c2;
`

const WriteDate = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: #838685;
`

const Body = styled.div`
  overflow: hidden;
  width: 100%;

  img,
  video {
    max-width: 100%;
    height: auto;
  }

  table {
    width: 100%;
  }

  pre,
  code {
    word-wrap: break-word;
    white-space: pre-wrap;
  }

  iframe {
    max-width: 100%;
  }

  @media ${theme.device.medium} {
    img,
    video {
      max-width: 100%;
      height: auto;
    }

    table {
      width: 100%;
    }

    pre,
    code {
      word-wrap: break-word;
      white-space: pre-wrap;
    }

    iframe {
      max-width: 100%;
    }
  }
`

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const NavigationBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;

  height: 45px;
`

const NavigationButtonBox = styled.div`
  cursor: pointer;

  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 114px;
  height: 45px;
  padding: 20px 10px;

  color: #838685;

  background-color: transparent;
  border: 1px solid #c1c3c2;
  border-radius: 12px;
`

const NavigationText = styled.span`
  overflow: hidden;

  max-width: 257px;

  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
  color: #313634;
  text-overflow: ellipsis;
  white-space: nowrap;
`
